import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  Eye,
  Loader2,
  Pencil,
  Plus,
  Save,
  Shield,
  SlidersHorizontal,
  UserMinus,
  Users,
  X,
} from 'lucide-react';

import {
  createTeam,
  getTeam,
  getTeams,
  getTeamUsers,
  initializeTeams,
  removeUserFromTeam,
  updateTeam,
  Team,
  ContactOwnershipVisibility,
  User,
} from '../services/api.ts';
import { getMenuPermissionTree } from '../config/sidebarMenu.tsx';
import { isUserMaster } from '../services/permissionService.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';

type TeamDialogMode = 'create' | 'edit' | 'view';

interface PermissionDraft {
  name: string;
  description: string;
  menuPermissions: string[];
  contactStages: string[];
  ownershipVisibility: ContactOwnershipVisibility;
  mcpEnabled: boolean;
}

interface UserItemProps {
  user: User;
  onRemove: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

const CONTACT_STAGE_OPTIONS = [
  { key: 'all', label: 'Todos', description: 'Acesso completo à lista de contatos' },
  { key: 'lead', label: 'Leads', description: 'Contatos no início do funil' },
  { key: 'agendado', label: 'Agendados', description: 'Leads com consulta agendada' },
  { key: 'perdido', label: 'Perdidos', description: 'Contatos marcados como perdidos' },
  { key: 'faltou', label: 'No-show', description: 'Pacientes que faltaram' },
  { key: 'compareceu', label: 'Compareceu', description: 'Pacientes que compareceram sem venda' },
  { key: 'venda', label: 'Venda', description: 'Pacientes com venda registrada' },
  { key: 'paciente', label: 'Pacientes', description: 'Pacientes cadastrados' },
  { key: 'contato', label: 'Contatos avulsos', description: 'Contatos sem lead ou paciente' },
];

const OWNERSHIP_OPTIONS: { value: ContactOwnershipVisibility; label: string; description: string }[] = [
  { value: 'own', label: 'Somente os próprios', description: 'Cada usuário vê somente os contatos atribuídos a ele, respeitando as etapas do funil abaixo.' },
  { value: 'unassigned', label: 'Somente não atribuídos', description: 'Exibe apenas contatos sem responsável. Ao assumir um atendimento, ele deixa de aparecer para o usuário.' },
  { value: 'own_and_unassigned', label: 'Próprios e não atribuídos', description: 'Cada usuário vê os próprios contatos e os sem responsável, respeitando as etapas do funil abaixo.' },
  { value: 'all', label: 'Todos', description: 'Inclui contatos atribuídos a outros usuários da clínica, respeitando as etapas do funil abaixo.' },
];

const DEFAULT_TEAM_CODES = new Set(['CRC', 'VENDAS', 'GERENTE', 'SUPORTE']);

const emptyDraft: PermissionDraft = {
  name: '',
  description: '',
  menuPermissions: [],
  contactStages: [],
  ownershipVisibility: 'own_and_unassigned',
  mcpEnabled: false,
};

const sectionNumberClasses = 'grid h-7 w-7 shrink-0 place-items-center rounded-full bg-sminy-primary text-xs font-bold text-white';

const isDefaultTeam = (team?: Team | null): boolean => {
  return Boolean(team?.code && DEFAULT_TEAM_CODES.has(team.code));
};

const makeDraftFromTeam = (team: Team): PermissionDraft => ({
  name: team.name,
  description: team.description || '',
  menuPermissions: (team.menu_permissions || []).filter(permission => permission !== 'mcp'),
  contactStages: team.contact_funnel_stages || [],
  ownershipVisibility: team.contact_ownership_visibility || 'own_and_unassigned',
  mcpEnabled: team.mcp_enabled === true,
});

const toggleMenuPermission = (permissions: string[], permission: string): string[] => {
  return permissions.includes(permission)
    ? permissions.filter(item => item !== permission)
    : [...permissions, permission];
};

const toggleContactStage = (stages: string[], stage: string): string[] => {
  if (stage === 'all') {
    return stages.includes('all') ? [] : ['all'];
  }

  const withoutAll = stages.filter(item => item !== 'all');
  return withoutAll.includes(stage)
    ? withoutAll.filter(item => item !== stage)
    : [...withoutAll, stage];
};

const contactSummary = (stages: string[]): string => {
  if (stages.includes('all')) {
    return 'Todos os contatos';
  }
  if (stages.length === 0) {
    return 'Sem acesso à lista de contatos';
  }
  return CONTACT_STAGE_OPTIONS
    .filter(option => stages.includes(option.key))
    .map(option => option.label)
    .join(', ');
};

const permissionCountLabel = (count: number): string => {
  return `${count} ${count === 1 ? 'permissão' : 'permissões'}`;
};

const UserItem: React.FC<UserItemProps> = ({ user, onRemove, isLoading, readOnly }) => {
  const { isDark } = useTheme();

  return (
    <div className={`flex items-center justify-between gap-3 p-4 transition ${isDark ? 'hover:bg-gray-700/30' : 'hover:bg-slate-50'}`}>
      <div className="flex min-w-0 items-center space-x-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium ${
          isDark ? 'bg-gray-700 text-gray-200' : 'bg-slate-200 text-slate-700'
        }`}>
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className={`truncate text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
          <p className={`truncate text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{user.email}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span className="rounded-full bg-sminy-primary/10 px-3 py-1 text-xs font-bold capitalize text-sminy-primary">
          {user.role}
        </span>

        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            disabled={isLoading}
            className={`rounded-xl bg-red-500 p-2.5 text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50`}
            title="Remover da equipe"
            aria-label={`Remover ${user.name} da equipe`}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

const TeamsManagement: React.FC = () => {
  const { isDark } = useTheme();
  const isMaster = isUserMaster();
  const menuPermissionTree = useMemo(() => getMenuPermissionTree().filter(item => item.key !== 'mcp'), []);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamUsers, setTeamUsers] = useState<User[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [permissionDraft, setPermissionDraft] = useState<PermissionDraft | null>(null);
  const [createDraft, setCreateDraft] = useState<PermissionDraft>(emptyDraft);
  const [dialogMode, setDialogMode] = useState<TeamDialogMode | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectingTeam, setIsSelectingTeam] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState<{ [key: number]: boolean }>({});

  const loadTeams = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      let response = await getTeams();
      if (response.length === 0) {
        await initializeTeams();
        response = await getTeams();
      }
      setTeams(response);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const closeTeamDialog = () => {
    if (isCreating || isSaving) return;

    setDialogMode(null);
    setSelectedTeam(null);
    setPermissionDraft(null);
    setTeamUsers([]);
    setLoadingUsers({});
  };

  const openCreateDialog = () => {
    setCreateDraft({ ...emptyDraft });
    setSelectedTeam(null);
    setPermissionDraft(null);
    setTeamUsers([]);
    setDialogMode('create');
  };

  const openTeamDialog = async (team: Team) => {
    const readOnly = isDefaultTeam(team) && !isMaster;
    setDialogMode(readOnly ? 'view' : 'edit');
    setSelectedTeam(team);
    setPermissionDraft(makeDraftFromTeam(team));
    setTeamUsers([]);
    setIsSelectingTeam(true);

    try {
      const teamDetails = await getTeam(team.id);
      const users = await getTeamUsers(team.id);
      setSelectedTeam(teamDetails);
      setPermissionDraft(makeDraftFromTeam(teamDetails));
      setTeamUsers(users);
    } catch (error) {
      console.error('Erro ao carregar detalhes da equipe:', error);
      try {
        const users = await getTeamUsers(team.id);
        setTeamUsers(users);
      } catch (usersError) {
        console.error('Erro ao carregar usuários da equipe:', usersError);
      }
    } finally {
      setIsSelectingTeam(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!createDraft.name.trim()) return;

    setIsCreating(true);
    try {
      await createTeam({
        name: createDraft.name,
        description: createDraft.description,
        menu_permissions: createDraft.menuPermissions,
        contact_funnel_stages: createDraft.contactStages,
        ...(teams.some(team => team.contact_ownership_visibility !== undefined)
          ? { contact_ownership_visibility: createDraft.ownershipVisibility } : {}),
        ...(isMaster ? { mcp_enabled: createDraft.mcpEnabled } : {}),
      });
      setCreateDraft({ ...emptyDraft });
      setDialogMode(null);
      await loadTeams(false);
    } catch (error) {
      console.error('Erro ao criar equipe:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveTeam = async () => {
    if (!selectedTeam || !permissionDraft || (isDefaultTeam(selectedTeam) && !isMaster)) return;

    setIsSaving(true);
    try {
      const updatedTeam = await updateTeam(selectedTeam.id, {
        ...(!isDefaultTeam(selectedTeam) ? {
          name: permissionDraft.name,
          description: permissionDraft.description,
        } : {}),
        menu_permissions: permissionDraft.menuPermissions,
        contact_funnel_stages: permissionDraft.contactStages,
        ...(selectedTeam.contact_ownership_visibility !== undefined
          ? { contact_ownership_visibility: permissionDraft.ownershipVisibility } : {}),
        ...(isMaster ? { mcp_enabled: permissionDraft.mcpEnabled } : {}),
      });
      setSelectedTeam(updatedTeam);
      setPermissionDraft(makeDraftFromTeam(updatedTeam));
      setDialogMode(null);
      await loadTeams(false);
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleUserInTeam = async (user: User) => {
    if (!selectedTeam) return;

    setLoadingUsers(prev => ({ ...prev, [user.id]: true }));

    try {
      await removeUserFromTeam(selectedTeam.id, user.id);
      const [users, teamDetails] = await Promise.all([
        getTeamUsers(selectedTeam.id),
        getTeam(selectedTeam.id),
      ]);
      setTeamUsers(users);
      setSelectedTeam(teamDetails);
      setPermissionDraft(makeDraftFromTeam(teamDetails));
      await loadTeams(false);
    } catch (error) {
      console.error('Erro ao atualizar usuário na equipe:', error);
    } finally {
      setLoadingUsers(prev => ({ ...prev, [user.id]: false }));
    }
  };

  const updateDraft = (updates: Partial<PermissionDraft>) => {
    setPermissionDraft(prev => prev ? { ...prev, ...updates } : prev);
  };

  const updateCreateDraft = (updates: Partial<PermissionDraft>) => {
    setCreateDraft(prev => ({ ...prev, ...updates }));
  };

  const renderMenuPermissionGrid = (
    selectedPermissions: string[],
    onTogglePermission: (permission: string) => void,
    readOnly = false,
  ) => (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      {menuPermissionTree.map(menu => {
        const isMenuEnabled = selectedPermissions.includes(menu.key);
        const enabledSubItems = menu.subItems.filter(subItem => selectedPermissions.includes(subItem.key)).length;
        const MenuIcon = menu.icon;
        return (
          <div
            key={menu.key}
            className={`rounded-xl border p-3 transition ${
              isMenuEnabled
                ? isDark
                  ? 'border-sminy-primary bg-sminy-primary/10'
                  : 'border-sminy-primary bg-blue-50'
                : isDark
                  ? 'border-gray-700 bg-gray-900/40'
                  : 'border-slate-200 bg-white'
            }`}
          >
            <button
              type="button"
              disabled={readOnly}
              onClick={() => onTogglePermission(menu.key)}
              className={`flex w-full items-start gap-3 rounded-lg p-1 text-left transition ${
                readOnly ? 'cursor-default' : ''
              }`}
            >
              <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border ${
                isMenuEnabled
                  ? 'border-sminy-primary bg-sminy-primary text-white'
                  : isDark
                    ? 'border-gray-600 text-transparent'
                    : 'border-slate-300 text-transparent'
              }`}>
                <Check className="h-3.5 w-3.5" />
              </span>
              {MenuIcon && <MenuIcon className={`mt-0.5 h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />}
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {menu.label}
                </span>
                <span className={`block text-xs leading-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Menu principal - {menu.section}
                  {menu.subItems.length > 0 ? ` - ${enabledSubItems}/${menu.subItems.length} submenus` : ''}
                </span>
              </span>
            </button>

            {menu.subItems.length > 0 && (
              <div className={`mt-3 space-y-2 border-t pt-3 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                {menu.subItems.map(subItem => {
                  const isSubItemEnabled = selectedPermissions.includes(subItem.key);
                  const SubIcon = subItem.icon;
                  return (
                    <button
                      key={subItem.key}
                      type="button"
                      disabled={readOnly}
                      onClick={() => onTogglePermission(subItem.key)}
                      className={`flex min-h-[44px] w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                        isSubItemEnabled
                          ? isDark
                            ? 'border-sminy-primary/70 bg-sminy-primary/10'
                            : 'border-sminy-primary/70 bg-sminy-primary/5'
                        : isDark
                          ? 'border-gray-700 bg-gray-900/40 hover:border-gray-600'
                          : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                      } ${readOnly ? 'cursor-default' : ''}`}
                    >
                      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border ${
                        isSubItemEnabled
                          ? 'border-sminy-primary bg-sminy-primary text-white'
                          : isDark
                            ? 'border-gray-600 text-transparent'
                            : 'border-slate-300 text-transparent'
                      }`}>
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {SubIcon && <SubIcon className={`h-4 w-4 shrink-0 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />}
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                          {subItem.label}
                        </span>
                        <span className={`block text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                          Submenu
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderContactStageGrid = (
    selectedStages: string[],
    onToggleStage: (stage: string) => void,
    readOnly = false,
  ) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {CONTACT_STAGE_OPTIONS.map(option => {
        const isEnabled = selectedStages.includes(option.key);
        return (
          <button
            key={option.key}
            type="button"
            disabled={readOnly}
            onClick={() => onToggleStage(option.key)}
            className={`min-h-[86px] rounded-xl border p-3 text-left transition ${
              isEnabled
                ? isDark
                  ? 'border-sminy-primary bg-sminy-primary/10'
                  : 'border-sminy-primary bg-blue-50'
                : isDark
                  ? 'border-gray-700 bg-gray-900/40 hover:border-gray-600'
                  : 'border-slate-200 bg-white hover:border-slate-300'
            } ${readOnly ? 'cursor-default' : ''}`}
          >
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 grid h-5 w-5 place-items-center rounded-md border ${
                isEnabled
                  ? 'border-sminy-primary bg-sminy-primary text-white'
                  : isDark
                    ? 'border-gray-600 text-transparent'
                    : 'border-slate-300 text-transparent'
              }`}>
                <Check className="h-3.5 w-3.5" />
              </span>
              <span>
                <span className={`block text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {option.label}
                </span>
                <span className={`block text-xs leading-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  {option.description}
                </span>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderTeamDialog = () => {
    if (!dialogMode) return null;

    const isCreateMode = dialogMode === 'create';
    const activeDraft = isCreateMode ? createDraft : permissionDraft;
    const selectedTeamIsDefault = !isCreateMode && isDefaultTeam(selectedTeam);
    const readOnly = dialogMode === 'view' || (selectedTeamIsDefault && !isMaster);
    const ownershipSupported = isCreateMode
      ? teams.some(team => team.contact_ownership_visibility !== undefined)
      : selectedTeam?.contact_ownership_visibility !== undefined;
    const title = isCreateMode
      ? 'Nova equipe personalizada'
      : selectedTeamIsDefault
      ? 'Equipe padrão'
      : 'Editar equipe';
    const subtitle = isCreateMode
      ? 'Defina identidade, menus, submenus e lista de contatos antes de salvar.'
      : selectedTeamIsDefault
      ? 'O master pode ajustar as permissões desta equipe para a clínica.'
      : 'Atualize identidade, permissões e revise os membros vinculados.';

    if (!activeDraft) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeTeamDialog} />
        <div className={`relative z-[10000] flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl shadow-xl ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className={`border-b px-5 py-4 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                  isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
                }`}>
                  {isCreateMode ? <Plus className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{subtitle}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {!isCreateMode && selectedTeamIsDefault && (
                      <span className="rounded-full bg-sminy-primary/10 px-2.5 py-1 font-semibold text-sminy-primary">Padrão</span>
                    )}
                    {!isCreateMode && (
                      <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                        {teamUsers.length || selectedTeam?.user_count || 0} {(teamUsers.length || selectedTeam?.user_count || 0) === 1 ? 'membro' : 'membros'}
                      </span>
                    )}
                    <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                      {permissionCountLabel(activeDraft.menuPermissions.length)}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                      {contactSummary(activeDraft.contactStages)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closeTeamDialog}
                className={`rounded-lg p-2 transition ${isDark ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}
                aria-label="Fechar modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto px-5 py-5">
            <div className="space-y-6">
              {selectedTeamIsDefault && (
                <div className={`rounded-xl border p-4 ${
                  isDark ? 'border-sminy-primary/30 bg-sminy-primary/10' : 'border-sminy-primary/20 bg-sminy-primary/5'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-sminy-primary/15 text-sminy-primary">
                      <Shield className="h-4 w-4" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Equipe padrão do sistema
                      </p>
                      <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {isMaster
                          ? 'Ajuste os menus, submenus e contatos disponíveis para os membros desta equipe. O nome e a identidade da equipe são preservados.'
                          : 'Somente o master pode alterar as permissões desta equipe padrão.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isSelectingTeam && !isCreateMode && (
                <div className="flex items-center gap-2 text-sm text-sminy-primary">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando equipe...
                </div>
              )}

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className={sectionNumberClasses}>1</span>
                  <div>
                    <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>Identidade da equipe</h4>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Nome claro ajuda na hora de atribuir usuários.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <label className="space-y-2">
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Nome</span>
                    <input
                      value={activeDraft.name}
                      onChange={(event) => isCreateMode ? updateCreateDraft({ name: event.target.value }) : updateDraft({ name: event.target.value })}
                      disabled={readOnly || selectedTeamIsDefault}
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-sminy-primary ${
                        isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-slate-200 bg-white text-slate-900'
                      } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
                      placeholder="Ex: Recepção tarde"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Descrição</span>
                    <input
                      value={activeDraft.description}
                      onChange={(event) => isCreateMode ? updateCreateDraft({ description: event.target.value }) : updateDraft({ description: event.target.value })}
                      disabled={readOnly || selectedTeamIsDefault}
                      className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition focus:border-sminy-primary ${
                        isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-slate-200 bg-white text-slate-900'
                      } ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}
                      placeholder="Escopo da equipe"
                    />
                  </label>
                </div>
              </section>

              <section className={`space-y-4 border-t pt-5 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <span className={sectionNumberClasses}>2</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                      <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>Menus e submenus da sidebar</h4>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Marque o menu principal e, quando existir, os submenus liberados.
                    </p>
                  </div>
                </div>
                {renderMenuPermissionGrid(activeDraft.menuPermissions, (permission) => {
                  if (isCreateMode) {
                    updateCreateDraft({ menuPermissions: toggleMenuPermission(createDraft.menuPermissions, permission) });
                    return;
                  }
                  updateDraft({ menuPermissions: toggleMenuPermission(activeDraft.menuPermissions, permission) });
                }, readOnly)}
              </section>

              <section className={`space-y-4 border-t pt-5 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <span className={sectionNumberClasses}>3</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                      <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>Lista de contatos</h4>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Defina quais etapas do funil aparecem para a equipe.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="team-ownership-visibility" className={`block text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    Visibilidade por responsável
                  </label>
                  <select
                    id="team-ownership-visibility"
                    aria-describedby="team-ownership-description"
                    value={activeDraft.ownershipVisibility}
                    disabled={readOnly || isSaving || isCreating || isSelectingTeam || !ownershipSupported}
                    onChange={event => {
                      const ownershipVisibility = event.target.value as ContactOwnershipVisibility;
                      if (isCreateMode) updateCreateDraft({ ownershipVisibility });
                      else updateDraft({ ownershipVisibility });
                    }}
                    className={`block min-h-[44px] w-full max-w-[480px] rounded-xl border px-3 py-2 text-sm outline-none focus:border-sminy-primary focus:ring-1 focus:ring-sminy-primary disabled:cursor-not-allowed disabled:opacity-60 ${
                      isDark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-slate-200 bg-white text-slate-900'
                    }`}
                  >
                    {OWNERSHIP_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <p id="team-ownership-description" className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                    {ownershipSupported
                      ? OWNERSHIP_OPTIONS.find(option => option.value === activeDraft.ownershipVisibility)?.description
                      : 'Esta configuração ainda não está disponível.'}
                  </p>
                </div>
                {renderContactStageGrid(activeDraft.contactStages, (stage) => {
                  if (isCreateMode) {
                    updateCreateDraft({ contactStages: toggleContactStage(createDraft.contactStages, stage) });
                    return;
                  }
                  updateDraft({ contactStages: toggleContactStage(activeDraft.contactStages, stage) });
                }, readOnly)}
              </section>

              <section className={`rounded-xl border p-4 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                <label className="flex min-h-[44px] items-center gap-3">
                  <input type="checkbox" checked={activeDraft.mcpEnabled}
                    disabled={!isMaster || isSaving || isCreating || isSelectingTeam}
                    onChange={event => isCreateMode
                      ? updateCreateDraft({ mcpEnabled: event.target.checked })
                      : updateDraft({ mcpEnabled: event.target.checked })}
                    className="h-5 w-5 shrink-0 accent-sminy-primary" />
                  <span>
                    <span className="block text-sm font-semibold">Acesso ao MCP</span>
                    <span className={`block text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Permite conectar assistentes de IA aos dados da própria clínica. Somente o master pode alterar esta opção.
                    </span>
                  </span>
                </label>
              </section>

              {!isCreateMode && (
                <section className={`overflow-hidden rounded-2xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
                  <div className={`border-b px-5 py-4 ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`grid h-9 w-9 place-items-center rounded-xl ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-slate-100 text-slate-600'}`}>
                        <Users className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Membros atuais</h4>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          {selectedTeam?.name || 'Equipe selecionada'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto">
                    {teamUsers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-2 px-5 py-8 text-center">
                        <div className={`grid h-12 w-12 place-items-center rounded-xl ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-500'}`}>
                          <Users className="h-5 w-5" />
                        </div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Nenhum usuário ainda</p>
                        <p className={`max-w-sm text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          Atribua usuários a esta equipe pela aba Usuários.
                        </p>
                      </div>
                    ) : (
                      <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-slate-100'}`}>
                        {teamUsers.map((user) => (
                          <UserItem
                            key={user.id}
                            user={user}
                            onRemove={() => handleToggleUserInTeam(user)}
                            isLoading={loadingUsers[user.id]}
                            readOnly={readOnly || selectedTeamIsDefault}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          </div>

          <div className={`flex flex-col-reverse gap-3 border-t px-5 py-4 sm:flex-row sm:justify-end ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
            <button
              type="button"
              onClick={closeTeamDialog}
              className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition ${
                isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Fechar
            </button>
            {isCreateMode ? (
              <button
                type="button"
                onClick={handleCreateTeam}
                disabled={isCreating || !createDraft.name.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sminy-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Criar equipe
              </button>
            ) : readOnly && !isMaster ? (
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-semibold text-white opacity-60"
              >
                <Shield className="h-4 w-4" />
                Padrão fixo
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSaveTeam}
                disabled={isSaving || isSelectingTeam || !permissionDraft?.name.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sminy-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar alterações
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-sminy-primary" />
          <p className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Carregando equipes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Equipes
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            Configure equipes e permissões de menus da sidebar.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDialog}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sminy-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Nova equipe
        </button>
      </div>

      <div className={`overflow-x-auto rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
        <table className="w-full min-w-[900px]">
          <thead className={isDark ? 'bg-gray-700/50' : 'bg-slate-50'}>
            <tr className={`text-left text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
              <th className="px-5 py-3 font-medium">Equipe</th>
              <th className="px-5 py-3 font-medium">Tipo</th>
              <th className="px-5 py-3 font-medium">Membros</th>
              <th className="px-5 py-3 font-medium">Permissões</th>
              <th className="px-5 py-3 font-medium">Contatos</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {teams.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-10">
                  <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                    <div className={`grid h-12 w-12 place-items-center rounded-xl ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-500'}`}>
                      <Users className="h-5 w-5" />
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Nenhuma equipe encontrada</p>
                    <p className={`max-w-sm text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Crie uma equipe personalizada para liberar menus e contatos.</p>
                    <button
                      type="button"
                      onClick={openCreateDialog}
                      className={`mt-2 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm transition hover:shadow ${
                        isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                      Nova equipe
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              teams.map((team) => {
                const teamIsDefault = isDefaultTeam(team);
                const memberCount = team.user_count || 0;
                const permissionCount = (team.menu_permissions || []).length;
                return (
                  <tr key={team.id} className={`border-t text-sm transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-700/30' : 'border-slate-100 text-slate-700 hover:bg-slate-50'}`}>
                    <td className="px-5 py-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                          teamIsDefault
                            ? 'bg-sminy-primary/10 text-sminy-primary'
                            : isDark
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Users className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.name}</p>
                          <p className={`max-w-[320px] truncate text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            {team.description || 'Equipe personalizada'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                        teamIsDefault
                          ? 'bg-sminy-primary/10 text-sminy-primary'
                          : isDark
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {teamIsDefault ? 'Padrão' : 'Personalizada'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                        {memberCount} {memberCount === 1 ? 'membro' : 'membros'}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                        {permissionCountLabel(permissionCount)}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <p className={`max-w-[260px] truncate text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        {contactSummary(team.contact_funnel_stages || [])}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => openTeamDialog(team)}
                        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                          isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {teamIsDefault && !isMaster ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        {teamIsDefault && !isMaster ? 'Ver' : 'Editar'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {renderTeamDialog()}
    </div>
  );
};

export default TeamsManagement;
